export type BrowserIdentityState = {
  status: 'authenticated' | 'anonymous' | 'unavailable';
  userToken: string;
};

type HotpadsIdentitySignal = {
  current?: BrowserIdentityState;
  overridden?: boolean;
  ready: Promise<BrowserIdentityState>;
  resolve: (identity: BrowserIdentityState) => boolean;
  override: (identity: BrowserIdentityState) => void;
  startedAt: number;
  reported?: boolean;
};

type IdentityWindow = Window & {
  __hotpadsIdentity?: HotpadsIdentitySignal;
};

const UNAVAILABLE_IDENTITY: BrowserIdentityState = { status: 'unavailable', userToken: '' };

function serializeForInlineScript(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

export function getIdentitySignalBootstrapScriptContents(initialIdentity?: BrowserIdentityState): string {
  const serializedIdentity = initialIdentity ? serializeForInlineScript(initialIdentity) : 'null';

  return `
    (() => {
      if (!window.__hotpadsIdentity) {
        let settle;
        let settled = false;
        const ready = new Promise((resolve) => { settle = resolve; });
        window.__hotpadsIdentity = {
          ready,
          startedAt: performance.now(),
          resolve(identity) {
            if (settled || this.overridden) return false;
            settled = true;
            this.current = identity;
            settle(identity);
            return true;
          },
          override(identity) {
            this.overridden = true;
            this.current = identity;
            if (settled) return;
            settled = true;
            settle(identity);
          }
        };
      }
      const initialIdentity = ${serializedIdentity};
      if (initialIdentity) window.__hotpadsIdentity.resolve(initialIdentity);
    })();
  `;
}

export function getIdentityResolutionScriptContents(
  identity: BrowserIdentityState,
  acceptedScript = '',
): string {
  return `if (window.__hotpadsIdentity?.resolve(${serializeForInlineScript(identity)})) {${acceptedScript}}`;
}

function createClientSignal(): HotpadsIdentitySignal {
  let settle: (identity: BrowserIdentityState) => void = () => undefined;
  let settled = false;
  const ready = new Promise<BrowserIdentityState>((resolve) => {
    settle = resolve;
  });

  return {
    ready,
    startedAt: performance.now(),
    resolve(identity) {
      if (settled || this.overridden) return false;
      settled = true;
      this.current = identity;
      settle(identity);
      return true;
    },
    override(identity) {
      this.overridden = true;
      this.current = identity;
      if (settled) return;
      settled = true;
      settle(identity);
    },
  };
}

export function getHotpadsIdentitySignal(): HotpadsIdentitySignal | undefined {
  if (typeof window === 'undefined') return undefined;

  const identityWindow = window as IdentityWindow;
  identityWindow.__hotpadsIdentity ??= createClientSignal();
  return identityWindow.__hotpadsIdentity;
}

export function getHotpadsIdentityReady(): Promise<BrowserIdentityState> {
  return getHotpadsIdentitySignal()?.ready ?? Promise.resolve(UNAVAILABLE_IDENTITY);
}

export function resolveHotpadsIdentity(identity: BrowserIdentityState): boolean {
  return getHotpadsIdentitySignal()?.resolve(identity) ?? false;
}

export function overrideHotpadsIdentity(identity: BrowserIdentityState): void {
  getHotpadsIdentitySignal()?.override(identity);
}
